import { useState, useEffect, Fragment, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Eye, EyeOff, Plus, ChevronDown, ChevronRight, Layers, Lock, Link,
  Trash2, Info, X, FileText, Download, Mail, Sparkles
} from "lucide-react";
import PartyField from "@/estimating/components/PartyField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import BudgetSummaryContent from "@/budget/components/BudgetSummaryContent";
import AITakeoffNotes from "@/budget/components/AITakeoffNotes";
import AITakeoffAddButton from "@/budget/components/AITakeoffAddButton";
import ImportSummaryPanel from "@/budget/components/ImportSummaryPanel";
import { useAuth } from "@/budget/contexts/AuthContext";

const API = `${window.location.origin}/api/budget`;

function fmt(n) {
  if (!n && n !== 0) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}

function calcScope(scope) {
  const totalModPct = (scope.modifiers || []).reduce((s, m) => s + (parseFloat(m.percentage) || 0), 0);
  let subtotal = 0;
  const items = (scope.line_items || []).map((item, _origIdx) => {
    const qty = parseFloat(item.qty) || 0;
    const base = parseFloat(item.base_price) || 0;
    const modifiedPrice = base * (1 + totalModPct);
    const total = item.unit?.toLowerCase() === "unit" ? 0 : qty * modifiedPrice;
    subtotal += total;
    return { ...item, _modified_price: modifiedPrice, _total: total, _origIdx };
  });
  return { items, subtotal, totalModPct };
}

// Group items by `group` field. Returns null if no item has a group (flat list).
function groupItems(items) {
  const hasGroups = items.some(i => i.group && String(i.group).trim());
  if (!hasGroups) return null;
  const groups = [];
  const map = new Map();
  items.forEach(item => {
    const g = (item.group || "").trim() || "Other";
    if (!map.has(g)) {
      const entry = { name: g, items: [], subtotal: 0 };
      map.set(g, entry);
      groups.push(entry);
    }
    const entry = map.get(g);
    entry.items.push(item);
    entry.subtotal += item._total;
  });
  return groups;
}

// ─── Scope Line Items Panel ───────────────────────────────────────────────────
function ScopePanel({ scope, onUpdate, onRemove, isAdmin, showPrices = true, onMoveFlag }) {
  const { items, subtotal, totalModPct } = calcScope(scope);
  const groups = groupItems(items);

  // Pending confirmation: null | {title, description, onConfirm}
  const [confirmAction, setConfirmAction] = useState(null);

  const updateItem = (idx, field, val) => {
    const updated = [...scope.line_items];
    updated[idx] = { ...updated[idx], [field]: val };
    onUpdate({ ...scope, line_items: updated });
  };

  const addCustomLine = (groupName = "") => {
    const newItem = {
      item_id: (crypto?.randomUUID?.() || `custom-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
      template_id: "",
      description: "",
      qty: 0,
      unit: "Each",
      base_price: 0,
      notes: "",
      group: groupName,
      responsibility: "",
    };
    onUpdate({ ...scope, line_items: [...scope.line_items, newItem] });
  };

  const removeItem = (idx) => {
    const item = scope.line_items[idx];
    const desc = (item?.description || "").trim() || "this custom line";
    setConfirmAction({
      title: "Remove line item?",
      description: `Are you sure you want to remove "${desc}"? This cannot be undone (Ctrl+Z within the editor can still undo).`,
      onConfirm: () => {
        const updated = scope.line_items.filter((_, i) => i !== idx);
        onUpdate({ ...scope, line_items: updated });
      },
    });
  };

  const updateModifier = (idx, val) => {
    const updated = [...scope.modifiers];
    updated[idx] = { ...updated[idx], percentage: parseFloat(val) / 100 || 0 };
    onUpdate({ ...scope, modifiers: updated });
  };

  const renderItemRow = (item) => {
    const idx = item._origIdx;
    const isCustom = !item.template_id;
    const flag = item.flag || null;
    return (
      <tr
        key={item.item_id}
        className={`border-b border-slate-100 hover:bg-slate-50/50 ${flag ? "bg-rose-50/60" : ""}`}
        data-testid={`line-item-row-${idx}`}
      >
        <td className={`px-3 py-2.5 text-slate-700 w-64 min-w-[16rem] ${flag ? "border-l-2 border-rose-400" : ""}`}>
          {flag && (
            <div className="flex items-start gap-1.5 mb-1" data-testid={`line-flag-${idx}`}>
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-rose-100 text-rose-700 text-[10px] font-semibold shrink-0">
                Flagged
              </span>
              <span className="text-[11px] text-rose-700 leading-snug">{flag.message}</span>
              {flag.suggested_scope_code && onMoveFlag && (
                <button
                  onClick={() => onMoveFlag({ ...item, scope_id: scope.scope_id, scope_name: scope.scope_name })}
                  className="shrink-0 px-1.5 py-0.5 rounded-sm border border-slate-300 bg-white text-[10px] font-medium text-slate-700 hover:bg-slate-50"
                  data-testid={`line-flag-move-${idx}`}
                >
                  Move to {flag.suggested_scope_name || flag.suggested_scope_code}
                </button>
              )}
            </div>
          )}
          {!!(item.source_rows || []).length && (
            <div className="text-[10px] text-slate-400 mb-1" title={item.source_rows.join(" · ")} data-testid={`line-source-${idx}`}>
              from {item.source_rows.slice(0, 2).join(" · ")}
              {item.source_rows.length > 2 ? ` +${item.source_rows.length - 2} more` : ""}
            </div>
          )}
          {isCustom ? (
            <div className="flex items-center gap-2">
              <button
                onClick={() => removeItem(idx)}
                className="p-1 text-slate-400 hover:text-red-500 rounded shrink-0"
                title="Remove line"
                data-testid={`remove-line-${idx}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
              <Input
                value={item.description || ""}
                onChange={e => updateItem(idx, "description", e.target.value)}
                placeholder="Custom line description"
                className="h-7 text-sm rounded-sm border-slate-200 focus:border-[#161616] focus:ring-1 focus:ring-[#161616]"
                data-testid={`description-input-${idx}`}
              />
            </div>
          ) : (
            item.description
          )}
        </td>
        <td className="px-3 py-2.5 w-20">
          <Input
            type="number"
            min="0"
            value={item.qty || ""}
            onChange={e => updateItem(idx, "qty", e.target.value)}
            className="h-7 w-16 text-right rounded-sm text-sm border-slate-200 focus:border-[#161616] focus:ring-1 focus:ring-[#161616] ml-auto block"
            placeholder="0"
            data-testid={`qty-input-${idx}`}
          />
        </td>
        <td className="px-3 py-2.5 text-slate-500 text-xs w-16">
          {isCustom ? (
            <Input
              value={item.unit || ""}
              onChange={e => updateItem(idx, "unit", e.target.value)}
              className="h-7 w-14 text-xs rounded-sm border-slate-200 focus:border-[#161616] focus:ring-1 focus:ring-[#161616]"
              placeholder="Each"
              data-testid={`unit-input-${idx}`}
            />
          ) : (
            item.unit
          )}
        </td>
        {showPrices && (
          <td className="px-3 py-2.5 td-num text-slate-500 text-xs hidden lg:table-cell w-24">
            {isCustom ? (
              <Input
                type="number"
                min="0"
                value={item.base_price || ""}
                onChange={e => updateItem(idx, "base_price", e.target.value)}
                className="h-7 w-20 text-right rounded-sm text-sm border-slate-200 focus:border-[#161616] focus:ring-1 focus:ring-[#161616] ml-auto block"
                data-testid={`base-price-input-${idx}`}
              />
            ) : (
              <span
                title="Pricing is managed globally. Admins can edit it in Admin → Pricing."
                className="inline-flex items-center gap-1 text-slate-500 cursor-not-allowed"
                data-testid={`base-price-locked-${idx}`}
              >
                <Lock className="w-3 h-3 opacity-60" />
                {item.base_price ? `$${item._modified_price.toFixed(2)}` : "—"}
              </span>
            )}
          </td>
        )}
        <td className="px-3 py-2.5 td-num font-medium text-slate-800 w-28" data-testid={`line-total-${idx}`}>
          {item._total > 0 ? fmt(item._total) : (item.qty > 0 && item.unit?.toLowerCase() === "unit" ? "Unit Pricing" : "—")}
        </td>
        <td className="px-3 py-2.5 w-full">
          {isAdmin ? (
            <Input
              value={item.notes || ""}
              onChange={e => updateItem(idx, "notes", e.target.value)}
              className="h-7 w-full text-xs rounded-sm border-slate-200 focus:border-[#161616] focus:ring-1 focus:ring-[#161616]"
              placeholder="Notes"
              data-testid={`notes-input-${idx}`}
            />
          ) : (
            <span className="text-xs text-slate-500" data-testid={`notes-ro-${idx}`}>{item.notes || ""}</span>
          )}
        </td>
      </tr>
    );
  };

  const renderAddLineRow = (groupName = "") => (
    <tr className="border-b border-slate-100">
      <td colSpan={baseColCount + 2} className="px-3 py-2">
        <button
          onClick={() => addCustomLine(groupName)}
          className="inline-flex items-center gap-1.5 text-xs text-[#161616] hover:text-[#303E41] font-medium px-2 py-1 rounded hover:bg-slate-100 transition-colors"
          data-testid={`add-line-btn-${groupName || "default"}`}
        >
          <Plus className="w-3.5 h-3.5" />
          Add line
        </button>
      </td>
    </tr>
  );

  // Column count for spanning helpers.
  // Counts columns to the LEFT of (and including) the last non-Total data col:
  // desc + qty + unit + (price?)  — Total is added separately as `+1`.
  const baseColCount = 3 + (showPrices ? 1 : 0);

  return (
    <div className="flex flex-col" data-testid={`scope-panel-${scope.scope_id}`}>
      {/* Scope header */}
      <div className="flex items-center justify-between px-4 py-4 bg-white border-b border-slate-200 shrink-0">
        <div className="flex items-center gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-[#161616] bg-[#161616]/10 px-2 py-0.5 rounded-sm">{scope.scope_code}</span>
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                {scope.scope_name}
              </h2>
            </div>
            {scope.division_name && <p className="text-xs text-slate-400 mt-0.5">{scope.division_name}</p>}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConfirmAction({
              title: "Remove this scope?",
              description: `Are you sure you want to remove "${scope.scope_name}" and all of its line items from this budget? Auto-save will persist the change; use Ctrl+Z to undo within the editor.`,
              onConfirm: () => onRemove(scope.scope_id),
            })}
            className="rounded-sm border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 gap-1.5 text-xs"
            data-testid={`remove-scope-${scope.scope_id}`}
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </Button>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-400">Scope Total</div>
          <div className="text-xl font-semibold text-[#161616]" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }} data-testid={`scope-subtotal-${scope.scope_id}`}>
            {fmt(subtotal)}
          </div>
        </div>
      </div>

      {/* Line items table */}
      <div className="overflow-auto flex-1 px-4 py-4">
        <table className="w-full text-sm border border-slate-200 rounded-sm overflow-hidden" data-testid="line-items-table">
          <thead>
            <tr className="bg-slate-50 border-b-2 border-slate-200">
              <th className="text-left px-3 py-2.5 text-xs text-slate-500 font-medium w-64 min-w-[16rem]">Item Description</th>
              <th className="text-right px-3 py-2.5 text-xs text-slate-500 font-medium whitespace-nowrap w-20">Qty</th>
              <th className="text-left px-3 py-2.5 text-xs text-slate-500 font-medium w-16">Unit</th>
              {showPrices && (
                <th className="text-right px-3 py-2.5 text-xs text-slate-500 font-medium whitespace-nowrap hidden lg:table-cell">Price</th>
              )}
              <th className="text-right px-3 py-2.5 text-xs text-slate-500 font-medium whitespace-nowrap w-28">Total</th>
              <th className="text-left px-3 py-2.5 text-xs text-slate-500 font-medium w-full">Notes</th>
            </tr>
          </thead>
          <tbody>
            {groups ? (
              groups.map(grp => (
                <Fragment key={grp.name}>
                  <tr className="bg-[#161616]/5 border-b border-[#161616]/20" data-testid={`group-header-${grp.name}`}>
                    <td colSpan={baseColCount + 2} className="px-3 py-2 text-xs font-semibold text-[#161616]">
                      {grp.name}
                    </td>
                  </tr>
                  {grp.items.map(renderItemRow)}
                  {renderAddLineRow(grp.name)}
                  <tr className="bg-slate-50/70 border-b border-slate-200" data-testid={`group-subtotal-${grp.name}`}>
                    <td colSpan={baseColCount - 1 + 1} className="px-3 py-2 text-right text-xs text-slate-500 font-medium hidden lg:table-cell">
                      {grp.name} Subtotal
                    </td>
                    <td colSpan={1} className="px-3 py-2 text-right text-xs text-slate-500 font-medium lg:hidden">
                      Subtotal
                    </td>
                    <td className="px-3 py-2 td-num font-semibold text-slate-800 text-xs">{fmt(grp.subtotal)}</td>
                    <td></td>
                  </tr>
                </Fragment>
              ))
            ) : (
              <>
                {items.map(renderItemRow)}
                {renderAddLineRow("")}
              </>
            )}
          </tbody>
          <tfoot>
            <tr className="bg-slate-50 border-t-2 border-slate-200">
              <td colSpan={baseColCount - 1 + 1} className="px-3 py-2.5 text-xs text-slate-500 hidden lg:table-cell"></td>
              <td className="px-3 py-2.5 td-num font-semibold text-slate-900 text-sm" data-testid={`scope-subtotal-cell-${scope.scope_id}`}>
                {fmt(subtotal)}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>

        {/* Modifiers */}
        <div className="mt-4">
          <div className="text-xs text-slate-500 font-medium mb-2">Contingency, Logistics & Travel</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {(scope.modifiers || []).map((mod, idx) => (
              <div key={mod.name} className="bg-slate-50 border border-slate-200 rounded-sm p-3" data-testid={`modifier-${mod.name}`}>
                <div className="text-xs text-slate-500 mb-1">{mod.label || mod.name}</div>
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.5"
                    value={((parseFloat(mod.percentage) || 0) * 100).toFixed(1)}
                    onChange={e => updateModifier(idx, e.target.value)}
                    className="h-7 flex-1 text-right rounded-sm text-sm border-slate-200 focus:border-[#161616] focus:ring-1 focus:ring-[#161616]"
                    data-testid={`modifier-input-${mod.name}`}
                  />
                  <span className="text-xs text-slate-500">%</span>
                </div>
                <div className="text-xs text-slate-400 mt-1 td-num">
                  {subtotal > 0 ? fmt(subtotal / (1 + totalModPct) * parseFloat(mod.percentage || 0)) : "—"}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Qualification text */}
        {scope.qualification_text && (
          <div className="mt-4 flex gap-2 bg-amber-50 border border-amber-100 rounded-sm p-3">
            <Info className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">{scope.qualification_text}</p>
          </div>
        )}
      </div>

      {/* Confirmation dialog for destructive actions inside this scope */}
      <AlertDialog open={!!confirmAction} onOpenChange={(o) => !o && setConfirmAction(null)}>
        <AlertDialogContent data-testid="scope-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmAction?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirmAction?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="scope-confirm-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                confirmAction?.onConfirm?.();
                setConfirmAction(null);
              }}
              className="bg-red-600 hover:bg-red-700"
              data-testid="scope-confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Scope Selector Modal ────────────────────────────────────────────────────
function ScopeSelector({ open, onClose, hierarchy, selectedIds, onAdd }) {
  const [expanded, setExpanded] = useState({});

  const toggle = id => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="scope-selector-modal">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>Add Scope Module</DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto space-y-1 pr-1">
          {hierarchy.map(div => (
            <div key={div.id}>
              <button
                onClick={() => toggle(div.id)}
                className="flex items-center gap-2 w-full px-2 py-1.5 text-left text-xs text-slate-500 font-medium hover:bg-slate-50 rounded"
              >
                {expanded[div.id] ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                {div.name}
              </button>
              {expanded[div.id] && (
                <div className="ml-4 space-y-0.5">
                  {div.sections?.map(sec => (
                    <div key={sec.id}>
                      {(sec.scopes?.length || 0) > 1 && (
                        <div className="px-2 py-1 text-xs text-slate-400 font-medium">{sec.code} — {sec.name}</div>
                      )}
                      {sec.scopes?.map(scope => (
                        <button
                          key={scope.id}
                          disabled={!scope.is_active || selectedIds.includes(scope.id)}
                          onClick={() => { onAdd(scope); onClose(); }}
                          className={`flex items-center justify-between w-full px-3 py-2 text-sm rounded text-left transition-colors ${
                            !scope.is_active
                              ? "text-slate-400 cursor-not-allowed"
                              : selectedIds.includes(scope.id)
                              ? "text-slate-400 cursor-not-allowed bg-slate-50"
                              : "text-slate-800 hover:bg-[#161616]/10 hover:text-[#161616] font-medium"
                          }`}
                          data-testid={`scope-option-${scope.id}`}
                        >
                          <span className="flex items-center gap-2">
                            <Layers className="w-3.5 h-3.5" />
                            {scope.full_name || scope.name}
                          </span>
                          {!scope.is_active && <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">Coming Soon</span>}
                          {selectedIds.includes(scope.id) && <span className="text-[10px] text-[#161616] bg-[#161616]/10 px-1.5 py-0.5 rounded">Added</span>}
                          {scope.is_active && !selectedIds.includes(scope.id) && (
                            <span className="flex items-center gap-1 text-[10px] text-[#161616]">
                              <Plus className="w-3 h-3" /> Add
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Budget Builder ─────────────────────────────────────────────────────
export default function BudgetBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin } = useAuth();
  const isNew = !id;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [hierarchy, setHierarchy] = useState([]);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [activeScope, setActiveScope] = useState(null);
  const [projectInfoOpen, setProjectInfoOpen] = useState(true);

  // Show/hide the Price column. Admin-only toggle. Default: hidden.
  // Persisted per-user via localStorage under `abs_bb_show_prices`.
  const [showPrices, setShowPrices] = useState(() => {
    try {
      return localStorage.getItem("abs_bb_show_prices") === "1";
    } catch { return false; }
  });
  const toggleShowPrices = () => {
    setShowPrices(prev => {
      const next = !prev;
      try { localStorage.setItem("abs_bb_show_prices", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  const [projectInfo, setProjectInfo] = useState({
    project_name: "", customer: "", prepared_by: "", site_location: "",
    plans_dated: "", bid_phase: "",
    start_date: "", completion_date: "",
    architect: "", architect_contact_name: "", architect_contact_email: "",
    notes: "",
  });
  const [selectedScopes, setSelectedScopes] = useState([]);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [isArchived, setIsArchived] = useState(false);
  // Pricing-update notice: backend reports stale scopes when admin has
  // edited master pricing since this budget's snapshot. User can apply or
  // dismiss (in-session only — banner reappears on next load if still stale).
  const [pricingStale, setPricingStale] = useState({ has_updates: false, stale_scopes: [] });
  const [pricingDismissed, setPricingDismissed] = useState(false);
  // Public share-link state for the current budget (set after Email PDF sends
  // or when the budget doc already has one). Empty string = no live link.
  const [shareToken, setShareToken] = useState("");
  const [aiMeta, setAiMeta] = useState(null);  // { scopes_extracted, line_items_extracted, line_items_matched, line_items_misc, notes[], filename }
  const [resolvedAINotes, setResolvedAINotes] = useState(new Set()); // Set<number> of note indices that are marked resolved
  // FIX-7 takeoff import: one summary per uploaded file + callout clarifications.
  const [importSummaries, setImportSummaries] = useState([]);
  const [clarifications, setClarifications] = useState([]);
  const [importPanelOpen, setImportPanelOpen] = useState(true);

  // Auto-resolve MISSING notes when the user types into the field they target.
  // Only MISSING notes with a `field` target get auto-resolved. Every other note
  // is manual-resolve only (user clicks the checkbox).
  useEffect(() => {
    if (!aiMeta?.notes) return;
    setResolvedAINotes(prev => {
      const next = new Set(prev);
      let changed = false;
      aiMeta.notes.forEach((n, i) => {
        if (n?.tag !== "MISSING") return;
        const field = n?.target?.kind === "field" ? n.target.field : null;
        if (!field) return;
        const val = (projectInfo[field] || "").toString().trim();
        if (val && !next.has(i)) { next.add(i); changed = true; }
        // Do NOT un-resolve if user deletes the value later — feels punitive.
      });
      return changed ? next : prev;
    });
  }, [aiMeta, projectInfo]);

  const toggleAINoteResolved = (i) => {
    setResolvedAINotes(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  // Handle click on an AI-note bullet — jump the user to the relevant input or scope tab.
  const navigateToAINoteTarget = (target) => {
    if (!target || !target.kind) return;
    if (target.kind === "field" && target.field) {
      setProjectInfoOpen(true);
      // Wait for the collapsible to expand, then focus + scroll into view.
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="project-info-${target.field}"]`);
        if (el) {
          try { el.focus({ preventScroll: true }); } catch { el.focus(); }
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 120);
    } else if (target.kind === "scope" && target.scope_code) {
      const sc = selectedScopes.find(s => s.scope_code === target.scope_code);
      if (sc) {
        setActiveScope(sc.scope_id);
        setTimeout(() => {
          const el = document.querySelector(`[data-testid="scope-tab-${sc.scope_id}"]`);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }, 80);
      }
    }
  };
  const [defaultQualification, setDefaultQualification] = useState("");

  // Refs used by the auto-save effect to keep the latest values without
  // re-creating the effect on every keystroke.
  const handleSaveRef = useRef(() => {});
  const autoSaveTimerRef = useRef(null);
  const initialLoadDoneRef = useRef(isNew);  // for new budgets, no fetch needed

  // ─── Undo history (Ctrl+Z / Cmd+Z) ───────────────────────────────────────
  // Stack of {selectedScopes, projectInfo} snapshots representing PRIOR
  // states. On every state change we push the *previous* state. Ctrl+Z pops
  // the top and restores it. Capped at 50 entries to avoid unbounded growth.
  const historyStackRef = useRef([]);
  const prevSnapshotRef = useRef(null);
  const isUndoingRef = useRef(false);
  const HISTORY_CAP = 50;

  // In-app back history: stack of undo callbacks. Pushed on meaningful UI
  // actions (adding a scope, switching active scope). Back button pops the
  // latest; if empty, navigates to the Dashboard.
  const navStackRef = useRef([]);
  const handleBack = () => {
    const stack = navStackRef.current;
    if (stack.length > 0) {
      const undo = stack.pop();
      undo();
    } else {
      navigate("/dashboard");
    }
  };

  // Fetch hierarchy
  useEffect(() => {
    axios.get(`${API}/spec-hierarchy`).then(r => {
      setHierarchy(r.data);
      // Pre-expand all divisions in selector
    }).catch(() => toast.error("Failed to load scope hierarchy"));
  }, []);

  // Fetch the global disclaimer (used at the bottom of Summary / Export PDF).
  useEffect(() => {
    axios.get(`${API}/settings`)
      .then(r => setDefaultQualification(r.data.default_qualification_text || ""))
      .catch(() => {});
  }, []);

  // Auto-populate Customer + Prepared By for new budgets from the logged-in user
  useEffect(() => {
    if (isNew && user) {
      setProjectInfo(prev => {
        const next = { ...prev };
        let changed = false;
        if (!next.customer && user.company_name) { next.customer = user.company_name; changed = true; }
        if (!next.prepared_by) {
          const full = `${user.first_name || ""} ${user.last_name || ""}`.trim();
          if (full) { next.prepared_by = full; changed = true; }
        }
        // Return the SAME object when nothing changed — the auth shim returns a
        // new `user` object every render, so an unconditional new object here
        // caused an infinite render loop ("Maximum update depth exceeded").
        return changed ? next : prev;
      });
    }
  }, [isNew, user]);

  // ─── AI Takeoff hydration ─────────────────────────────────────────────
  // If the user arrived here from the AI Takeoff dialog, `location.state.aiTakeoff`
  // carries a pre-parsed { project_info, selected_scopes, ai_meta } payload.
  // Hydrate the Builder with it once, then clear the router state so a refresh
  // doesn't re-apply stale AI output.
  const aiHydratedRef = useRef(false);
  useEffect(() => {
    if (!isNew) return;
    if (aiHydratedRef.current) return;
    const payload = location.state?.aiTakeoff;
    if (!payload) return;
    aiHydratedRef.current = true;

    const info = payload.project_info || {};
    setProjectInfo(prev => ({
      ...prev,
      // Never overwrite the auto-filled customer with a blank
      ...Object.fromEntries(Object.entries(info).filter(([, v]) => v)),
    }));

    const scopes = payload.selected_scopes || [];
    setSelectedScopes(scopes);
    if (scopes.length > 0) setActiveScope(scopes[0].scope_id);

    const meta = payload.ai_meta || {};
    setAiMeta(meta);
    if (payload.import_summary) setImportSummaries([payload.import_summary]);
    if (payload.clarifications?.length) setClarifications(payload.clarifications);
    toast.success(
      `AI pre-filled ${meta.scopes_extracted || scopes.length} scope${(meta.scopes_extracted || scopes.length) === 1 ? "" : "s"} · ${meta.line_items_matched || 0} line items matched${meta.line_items_misc ? ` · ${meta.line_items_misc} misc` : ""}`,
      { duration: 6000 },
    );
    // Clear the router state to prevent re-hydration on refresh / back-nav
    navigate(location.pathname, { replace: true, state: null });
  }, [isNew, location.state, location.pathname, navigate]);

  // Merge an ADDITIONAL takeoff into the current budget. The uploaded file is
  // treated as extra scopes/lines for the SAME project — so project_info is
  // never overwritten.
  //
  // FIX-7 precedence (quantities are NEVER summed):
  //   · re-importing the SAME file replaces that file's contribution per scope
  //     (lines it previously wrote and no longer covers go back to 0),
  //   · a DIFFERENT file replaces the quantity on each (scope, line) it covers
  //     and leaves every other line alone,
  //   · a hand-typed quantity (no import_file) is only replaced when the new
  //     file actually covers that line.
  const mergeAdditionalTakeoff = (payload, uploadedFilename) => {
    if (!payload) return;
    const newScopes = payload.selected_scopes || [];
    if (newScopes.length === 0) {
      toast.info("No new scopes were detected in that file.");
      return;
    }
    const file = payload.import_summary?.file || payload.ai_meta?.filename || uploadedFilename || "";

    setSelectedScopes(prev => {
      const bySc = new Map(prev.map(s => [s.scope_code, { ...s, line_items: [...(s.line_items || [])], modifiers: [...(s.modifiers || [])] }]));
      newScopes.forEach(ns => {
        if (!bySc.has(ns.scope_code)) {
          bySc.set(ns.scope_code, { ...ns, line_items: [...(ns.line_items || [])] });
          return;
        }
        const cur = bySc.get(ns.scope_code);
        const incomingTpls = new Set((ns.line_items || []).filter(li => li.template_id && parseFloat(li.qty) > 0).map(li => li.template_id));

        // Re-import of the same file: clear what that file previously wrote and
        // no longer covers, plus its own flagged/misc lines (they are rebuilt).
        cur.line_items = cur.line_items.filter(li => {
          if (li.import_file !== file) return true;
          if (!li.template_id) return false;              // old flagged line from this file
          return true;
        }).map(li => {
          if (li.import_file === file && li.template_id && !incomingTpls.has(li.template_id)) {
            return { ...li, qty: 0, import_file: "", source_rows: [], import_match: "", flag: null };
          }
          return li;
        });

        const curByTpl = new Map();
        cur.line_items.forEach((li, idx) => { if (li.template_id) curByTpl.set(li.template_id, idx); });

        (ns.line_items || []).forEach(nli => {
          const incomingQty = parseFloat(nli.qty) || 0;
          if (nli.template_id && curByTpl.has(nli.template_id)) {
            const idx = curByTpl.get(nli.template_id);
            if (incomingQty > 0) {
              // REPLACE quantities, never sum — but Notes belong to the estimator.
              cur.line_items[idx] = { ...cur.line_items[idx], ...nli, notes: cur.line_items[idx].notes || "" };
            }
          } else if (incomingQty > 0 || !nli.template_id) {
            cur.line_items.push({ ...nli });
            if (nli.template_id) curByTpl.set(nli.template_id, cur.line_items.length - 1);
          }
        });
      });
      return Array.from(bySc.values());
    });

    // Import summary: one entry per file; a re-import replaces its own entry.
    if (payload.import_summary) {
      setImportSummaries(prev => {
        const without = prev.filter(s => s.file !== payload.import_summary.file);
        return [...without, payload.import_summary];
      });
      setImportPanelOpen(true);
    }
    if (payload.clarifications?.length) {
      setClarifications(prev => {
        const keep = prev.filter(c => c.source_file !== file);
        return [...keep, ...payload.clarifications];
      });
    }

    // Append the new file's notes onto the existing AI meta panel, tagged with
    // the source filename so reviewers can see which upload produced what.
    setAiMeta(prev => {
      const prevNotes = prev?.notes || [];
      const meta = payload.ai_meta || {};
      const label = uploadedFilename || meta.filename || "additional file";
      const addNotes = (meta.notes || []).map(n => ({
        ...n,
        text: `[${label}] ${n?.text || ""}`.trim(),
      }));
      return {
        ...(prev || {}),
        scopes_extracted: (prev?.scopes_extracted || 0) + (meta.scopes_extracted || 0),
        line_items_extracted: (prev?.line_items_extracted || 0) + (meta.line_items_extracted || 0),
        line_items_matched: (prev?.line_items_matched || 0) + (meta.line_items_matched || 0),
        line_items_misc: (prev?.line_items_misc || 0) + (meta.line_items_misc || 0),
        notes: [...prevNotes, ...addNotes],
        filename: prev?.filename ? `${prev.filename} + ${label}` : label,
      };
    });
  };

  // Move a flagged line into the scope the matcher suggested. Never automatic —
  // this is the one-click on the flag. The scope is added to the budget first
  // if it isn't there yet; the line arrives as a custom line with its source
  // rows intact so the quantity still traces to the file.
  const moveFlaggedLine = async (line) => {
    const targetCode = line.flag?.suggested_scope_code;
    if (!targetCode) return;
    let target = selectedScopes.find(s => s.scope_code === targetCode);
    if (!target) {
      try {
        const { data } = await axios.get(`${API}/admin/scopes`);
        const def = (data || []).find(s => s.code === targetCode);
        if (!def) { toast.error(`Scope ${targetCode} is not configured.`); return; }
        await addScope({ id: def.id, code: def.code });
        target = { scope_code: targetCode };
      } catch {
        toast.error("Could not open that scope");
        return;
      }
    }
    setSelectedScopes(prev => prev.map(s => {
      if (s.scope_id === line.scope_id) {
        return { ...s, line_items: (s.line_items || []).filter(li => li.item_id !== line.item_id) };
      }
      if (s.scope_code === targetCode) {
        return {
          ...s,
          line_items: [...(s.line_items || []), {
            ...line,
            flag: null,
          }],
        };
      }
      return s;
    }));
    toast.success(`Moved "${line.description}" to ${line.flag.suggested_scope_name || targetCode}`);
  };
  useEffect(() => {
    if (!isNew) {
      axios.get(`${API}/budgets/${id}`).then(r => {
        const b = r.data;
        setIsArchived(!!b.deleted_at);
        // Migrate legacy field names from existing budgets so renamed fields
        // (Plan Version → Bid Phase, Contact → Architect) display their data.
        const rawInfo = b.project_info || {};
        const loadedProjectInfo = {
          ...rawInfo,
          bid_phase: rawInfo.bid_phase || rawInfo.plan_version || "",
          architect: rawInfo.architect || rawInfo.contact || "",
        };
        const loadedScopes = b.selected_scopes || [];
        const loadedActive = loadedScopes.length > 0 ? loadedScopes[0].scope_id : null;
        setProjectInfo(loadedProjectInfo);
        setSelectedScopes(loadedScopes);
        setActiveScope(loadedActive);
        setShareToken(b.share_token || "");
        setClarifications(b.clarifications || []);
        setImportSummaries(b.import_summaries || []);
        // Seed the prev-snapshot baseline with the loaded data so the first
        // user edit pushes the loaded state (not an undefined/empty one) onto
        // the undo history. Flip the load-done flag here (same .then microtask
        // as the setters) so the auto-save effect doesn't transiently push a
        // phantom snapshot before the load is fully committed.
        prevSnapshotRef.current = {
          selectedScopes: loadedScopes,
          projectInfo: loadedProjectInfo,
          activeScope: loadedActive,
        };
        initialLoadDoneRef.current = true;
      }).catch(() => toast.error("Budget not found")).finally(() => {
        setLoading(false);
      });
      // Check whether master pricing has been updated since this budget's
      // last sync. Independent of the budget fetch so it can be re-run.
      axios.get(`${API}/budgets/${id}/pricing-status`)
        .then(r => setPricingStale(r.data))
        .catch(() => {/* non-fatal */});
    } else {
      // New budget: baseline is the initial empty state.
      prevSnapshotRef.current = {
        selectedScopes,
        projectInfo,
        activeScope,
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  // Keep a ref pointer to the latest handleSave so the auto-save effect can
  // call it without depending on it (which would recreate the effect every
  // render).
  // handleSaveRef.current is set after handleSave is declared below.

  // Debounced auto-save: trigger 800ms after the last change to selectedScopes
  // or projectInfo. Skips initial load and avoids creating empty draft budgets.
  useEffect(() => {
    if (!initialLoadDoneRef.current) return;
    // Determine whether anything actually changed since the previous snapshot.
    // React.StrictMode (and post-load state hydration) can re-run the effect
    // with the exact same state references; treat that as a no-op so we don't
    // push phantom undo entries or fire spurious auto-saves.
    let unchanged = false;
    if (prevSnapshotRef.current && !isUndoingRef.current) {
      const prev = prevSnapshotRef.current;
      unchanged =
        prev.selectedScopes === selectedScopes &&
        prev.projectInfo === projectInfo &&
        prev.activeScope === activeScope;
    }
    if (unchanged) return;

    // Track undo history: push the previous snapshot before this change,
    // unless the change came from an undo itself.
    if (isUndoingRef.current) {
      isUndoingRef.current = false;
    } else if (prevSnapshotRef.current) {
      historyStackRef.current.push(prevSnapshotRef.current);
      if (historyStackRef.current.length > HISTORY_CAP) {
        historyStackRef.current.shift();
      }
    }
    prevSnapshotRef.current = { selectedScopes, projectInfo, activeScope };

    // Don't auto-persist an empty budget (no scopes). This covers both the
    // initial /budgets/new state AND the case where the user undoes back to
    // an empty scope list after the URL has already been replaced to
    // /budgets/{id} (isNew=false).
    if (selectedScopes.length === 0) return;
    // Don't auto-save archived budgets — they're read-only until restored.
    if (isArchived) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      handleSaveRef.current(null, { silent: true });
    }, 800);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScopes, projectInfo, activeScope]);

  // Keyboard shortcut: Ctrl+Z (Win/Linux) and Cmd+Z (Mac) → undo last change.
  useEffect(() => {
    const onKey = (e) => {
      const isUndo = (e.ctrlKey || e.metaKey) && !e.shiftKey && (e.key === "z" || e.key === "Z");
      if (!isUndo) return;
      // Ignore if user is composing in an input/textarea: native input undo
      // should keep working for text fields.
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if (historyStackRef.current.length === 0) return;
      e.preventDefault();
      const prev = historyStackRef.current.pop();
      isUndoingRef.current = true;
      // If a debounced auto-save is pending from the original action, cancel
      // it so a stale write doesn't race the undo.
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      setSelectedScopes(prev.selectedScopes);
      setProjectInfo(prev.projectInfo);
      // Restore activeScope, falling back to the first remaining scope if the
      // previously active scope no longer exists in the restored set.
      const prevActiveExists = prev.selectedScopes.some(s => s.scope_id === prev.activeScope);
      const nextActive = prevActiveExists
        ? prev.activeScope
        : (prev.selectedScopes[0]?.scope_id || null);
      setActiveScope(nextActive);
      toast.success("Undone");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const grandTotal = selectedScopes.reduce((s, sc) => s + calcScope(sc).subtotal, 0);

  // Every flagged line across the budget, for the Import Summary panel.
  const importFlags = selectedScopes.flatMap(sc =>
    (sc.line_items || []).filter(li => li.flag).map(li => ({
      ...li, scope_id: sc.scope_id, scope_name: sc.scope_name,
    })));

  const addScope = async (scopeDef) => {
    try {
      const { data } = await axios.get(`${API}/scopes/${scopeDef.id}`);
      const modifiers = (data.pricing_rules || []).map(r => ({
        name: r.name, label: r.label || r.name,
        percentage: r.default_percentage || 0,
      }));
      const lineItems = (data.line_item_templates || []).map(t => ({
        item_id: t.id,
        template_id: t.id,
        description: t.description,
        qty: 0,
        unit: t.unit,
        base_price: t.base_price,
        notes: "",
        group: t.group || "",
        responsibility: t.responsibility || "",
      }));
      const newScope = {
        scope_id: data.id,
        scope_name: data.name,
        scope_code: data.code,
        section_code: scopeDef.code || "",
        division_name: "",
        qualification_text: data.qualification_text || "",
        line_items: lineItems,
        modifiers,
        subtotal: 0,
      };
      setSelectedScopes(prev => [...prev, newScope]);
      const prevActive = activeScope;
      setActiveScope(data.id);
      // After a scope is added, the prior UI state was the open scope selector.
      // Back should reopen it (and restore the prior active scope so the user
      // sees what they were viewing before they opened the selector).
      navStackRef.current.push(() => {
        setSelectorOpen(true);
        if (prevActive !== null) setActiveScope(prevActive);
      });
    } catch {
      toast.error("Failed to load scope");
    }
  };

  const removeScope = (scopeId) => {
    setSelectedScopes(prev => prev.filter(s => s.scope_id !== scopeId));
    setActiveScope(prev => prev === scopeId ? (selectedScopes.find(s => s.scope_id !== scopeId)?.scope_id || null) : prev);
  };

  const updateScope = (updated) => {
    setSelectedScopes(prev => prev.map(s => s.scope_id === updated.scope_id ? updated : s));
  };

  const handleSave = async (newStatus = null, { silent = false } = {}) => {
    setSaving(true);
    try {
      const payload = {
        project_info: projectInfo,
        selected_scopes: selectedScopes.map(sc => {
          const { subtotal } = calcScope(sc);
          // Sanitize numeric fields - empty strings/NaN must become 0 so Pydantic
          // can coerce them to float on the backend (otherwise save returns 422).
          const cleanedItems = (sc.line_items || []).map(it => ({
            ...it,
            qty: parseFloat(it.qty) || 0,
            base_price: parseFloat(it.base_price) || 0,
          }));
          const cleanedModifiers = (sc.modifiers || []).map(m => ({
            ...m,
            percentage: parseFloat(m.percentage) || 0,
          }));
          return { ...sc, line_items: cleanedItems, modifiers: cleanedModifiers, subtotal };
        }),
        grand_total: grandTotal,
        status: newStatus || (isNew ? "draft" : undefined),
        created_by: isAdmin ? "admin" : "estimator",
        clarifications,
        import_summaries: importSummaries,
      };

      if (isNew) {
        const { data } = await axios.post(`${API}/budgets`, payload);
        if (!silent) toast.success("Budget saved");
        setLastSavedAt(new Date());
        navigate(`/budgets/${data.id}`, { replace: true });
        return data.id;
      } else {
        await axios.put(`${API}/budgets/${id}`, payload, { params: { user_name: isAdmin ? "admin" : "estimator" } });
        if (!silent) toast.success("Budget saved");
        setLastSavedAt(new Date());
        return id;
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
        ? detail.map(d => d.msg || JSON.stringify(d)).join("; ")
        : err?.message || "Failed to save budget";
      if (!silent) toast.error(msg);
      console.error("Save budget error:", err?.response?.data || err);
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Keep ref in sync so the auto-save effect always calls the latest version.
  handleSaveRef.current = handleSave;

  const activeScopeData = selectedScopes.find(s => s.scope_id === activeScope);

  // Live snapshot of the budget for the popout Summary modal and Email PDF
  // (uses in-memory state rather than re-fetching).
  const liveBudget = {
    id,
    project_info: projectInfo,
    selected_scopes: selectedScopes,
    grand_total: grandTotal,
    updated_at: lastSavedAt ? lastSavedAt.toISOString() : new Date().toISOString(),
  };

  // Export PDF: temporarily set document.title to the project name + site
  // location so the browser's print dialog defaults the filename to the
  // budget name. Then trigger native print. The page renders a hidden
  // `.print-only` container (below the main UI) that becomes visible at
  // print-time so ALL of the summary content is included across pages.
  const handleExportPdf = () => {
    const name = (projectInfo.project_name || "Budget").trim();
    const loc = (projectInfo.site_location || "").trim();
    const safeTitle = loc ? `${name} - ${loc}` : name;
    const prevTitle = document.title;
    document.title = safeTitle;
    requestAnimationFrame(() => {
      window.print();
      setTimeout(() => { document.title = prevTitle; }, 100);
    });
  };

  const [emailSending, setEmailSending] = useState(false);
  const [emailMessage, setEmailMessage] = useState("");

  const handleEmailSend = async () => {
    if (!emailTo.trim()) {
      toast.error("Recipient email is required");
      return;
    }
    if (!id) {
      toast.error("Save the budget before emailing");
      return;
    }
    setEmailSending(true);
    try {
      const { data } = await axios.post(`${API}/budgets/${id}/email`, {
        to: emailTo.trim(),
        cc: emailCc.trim(),
        message: emailMessage.trim(),
      });
      if (data?.share_url) {
        const t = data.share_url.split("/share/b/")[1] || "";
        if (t) setShareToken(t);
      }
      toast.success("Budget emailed");
      setEmailModalOpen(false);
      setEmailMessage("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send email");
    } finally {
      setEmailSending(false);
    }
  };

  // Apply updated master pricing into this budget. Re-pulls the budget
  // afterwards so all state (totals, prices) refreshes from the new data.
  const handleRefreshPricing = async () => {
    try {
      const role = isAdmin ? "admin" : "estimator";
      const { data } = await axios.post(`${API}/budgets/${id}/refresh-pricing`, null, { params: { user_name: role } });
      const b = data.budget;
      // Re-hydrate scope state from the refreshed budget. Keeps activeScope.
      setSelectedScopes(b.selected_scopes || []);
      setPricingStale({ has_updates: false, stale_scopes: [] });
      setPricingDismissed(false);
      toast.success(`Pricing refreshed (${data.refreshed_count} item${data.refreshed_count === 1 ? "" : "s"} updated)`);
    } catch {
      toast.error("Failed to refresh pricing");
    }
  };

  if (loading) return <div className="p-8 text-slate-400">Loading budget...</div>;

  return (
    <div className="flex h-full overflow-hidden" data-testid="budget-builder">
      {/* Left sidebar */}
      <div className="w-72 bg-white border-l border-r border-slate-200 flex flex-col overflow-hidden shrink-0" data-testid="project-info-column">
        {/* Project info */}
        <Collapsible open={projectInfoOpen} onOpenChange={setProjectInfoOpen}>
          <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-3 border-b border-slate-200 hover:bg-slate-50 transition-colors" data-testid="project-info-toggle">
            <span className="text-xs text-slate-500 font-medium">Project Info</span>
            {projectInfoOpen ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="p-4 space-y-3 border-b border-slate-200 max-h-[50vh] overflow-y-auto" data-testid="project-info-form">
              {(() => {
                const fields = [
                  { key: "project_name", label: "Project Name", type: "text", required: true },
                  { key: "customer", label: "Customer", type: "party", party: "gc" },
                  { key: "prepared_by", label: "Prepared By", type: "text", placeholder: "Auto-filled from your account" },
                  { key: "site_location", label: "Site Location", type: "text", required: true, placeholder: "City, State" },
                  { key: "plans_dated", label: "Plans Dated", type: "date" },
                  { key: "bid_phase", label: "Plan Version", type: "text" },
                  { key: "start_date", label: "Start Date", type: "date" },
                  { key: "completion_date", label: "Completion Date", type: "date" },
                  { key: "architect", label: "Architect", type: "party", party: "architect" },
                  { key: "architect_contact_name", label: "Architect Contact", type: "text", placeholder: "Contact name — syncs to Basis of Design" },
                  { key: "architect_contact_email", label: "Architect Email", type: "email", placeholder: "email@firm.com — syncs to Basis of Design" },
                ];
                return fields.map((f) => {
                  const { key, label, type, required, placeholder } = f;
                  const value = projectInfo[key] || "";
                  const isMissing = required && !value.trim();
                  const isBadFormat = key === "site_location" && value.trim() && !value.includes(",");
                  const invalid = isMissing || isBadFormat;
                  return (
                    <div key={key}>
                      <Label className="text-xs text-slate-500 mb-1 block">
                        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
                      </Label>
                      {type === "party" ? (
                        <PartyField value={value} type={f.party || "gc"} placeholder={label} testId={`project-info-${key}`}
                                    onChange={(name, company) => setProjectInfo(prev => ({ ...prev, [key]: name, [`${key}_company_id`]: company?.id || prev[`${key}_company_id`] || null }))} />
                      ) : (
                      <Input
                        type={type}
                        value={value}
                        onChange={e => setProjectInfo(prev => ({ ...prev, [key]: e.target.value }))}
                        className={`h-7 text-sm rounded-sm focus:ring-1 ${
                          invalid
                            ? "border-red-300 focus:border-red-400 focus:ring-red-200"
                            : "border-slate-200 focus:border-[#161616] focus:ring-[#161616]"
                        }`}
                        placeholder={placeholder || label}
                        data-testid={`project-info-${key}`}
                      />)}
                      {isBadFormat && (
                        <p className="text-[10px] text-red-500 mt-0.5">Format: City, State</p>
                      )}
                    </div>
                  );
                });
              })()}
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Notes</Label>
                <Textarea
                  value={projectInfo.notes || ""}
                  onChange={e => setProjectInfo(prev => ({ ...prev, notes: e.target.value }))}
                  className="text-sm rounded-sm border-slate-200 focus:border-[#161616] focus:ring-1 focus:ring-[#161616] min-h-[60px]"
                  placeholder="Notes"
                  data-testid="project-info-notes"
                />
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Scope selector */}
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <span className="text-xs text-slate-500 font-medium">Scopes</span>
          <button
            onClick={() => setSelectorOpen(true)}
            className="flex items-center gap-1 text-xs text-[#161616] hover:text-[#303E41] font-medium"
            data-testid="add-scope-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Add Scope
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {selectedScopes.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <Layers className="w-6 h-6 text-slate-300 mx-auto mb-2" />
              <p className="text-xs text-slate-400">No scopes added yet</p>
              <button onClick={() => setSelectorOpen(true)} className="text-xs text-[#161616] mt-1 hover:underline" data-testid="add-first-scope-link">Add your first scope</button>
            </div>
          ) : (
            <div className="py-1">
              {selectedScopes.map(sc => {
                const { subtotal } = calcScope(sc);
                return (
                  <button
                    key={sc.scope_id}
                    onClick={() => setActiveScope(sc.scope_id)}
                    className={`flex items-center justify-between w-full px-4 py-2.5 text-left transition-colors border-l-2 ${
                      activeScope === sc.scope_id
                        ? "border-[#161616] bg-[#161616]/5 text-[#161616]"
                        : "border-transparent text-slate-600 hover:bg-slate-50"
                    }`}
                    data-testid={`scope-tab-${sc.scope_id}`}
                  >
                    <div>
                      <div className="text-sm font-medium">{sc.scope_name}</div>
                      <div className="text-xs text-slate-400">{sc.scope_code}</div>
                    </div>
                    <div className="text-xs font-medium td-num">{fmt(subtotal)}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Grand total */}
        <div className="px-4 py-3 bg-slate-50 border-t-2 border-slate-200 shrink-0" data-testid="grand-total-sidebar">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500 font-medium">Grand Total</span>
            <span className="text-lg font-semibold text-slate-900 td-num" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>{fmt(grandTotal)}</span>
          </div>
        </div>
      </div>

      {/* Right main area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {isArchived && (
          <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 flex items-center justify-between gap-3" data-testid="archived-banner">
            <div className="text-xs text-amber-800">
              <span className="font-semibold">This budget is archived.</span> It is read-only. Restore it from Admin → Archive to make changes.
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  await axios.post(`${API}/budgets/${id}/restore`, null, { params: { user_name: isAdmin ? "admin" : "estimator" } });
                  setIsArchived(false);
                  toast.success("Budget restored");
                } catch {
                  toast.error("Failed to restore budget");
                }
              }}
              className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white border-0 text-xs h-7"
              data-testid="restore-archived-btn"
            >
              Restore
            </Button>
          </div>
        )}
        {pricingStale.has_updates && !pricingDismissed && !isArchived && (
          <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 flex items-center justify-between gap-3" data-testid="pricing-stale-banner">
            <div className="text-xs text-blue-800">
              <span className="font-semibold">Pricing has been updated.</span>{" "}
              Master pricing has changed for {pricingStale.stale_scopes.length} scope{pricingStale.stale_scopes.length === 1 ? "" : "s"}
              {pricingStale.stale_scopes.length <= 3 && ` (${pricingStale.stale_scopes.map(s => s.scope_name).join(", ")})`}.
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="nav"
                size="sm"
                onClick={() => setPricingDismissed(true)}
                className="rounded-sm text-xs h-7"
                data-testid="dismiss-pricing-update-btn"
              >
                Dismiss
              </Button>
              <Button
                size="sm"
                onClick={handleRefreshPricing}
                className="rounded-sm text-xs h-7"
                data-testid="apply-pricing-update-btn"
              >
                Apply updates
              </Button>
            </div>
          </div>
        )}
        {importPanelOpen && (importSummaries.length > 0 || clarifications.length > 0) && (
          <ImportSummaryPanel
            summaries={importSummaries}
            clarifications={clarifications}
            flags={importFlags}
            onDismiss={() => setImportPanelOpen(false)}
            onJumpToFlag={(f) => {
              const sc = selectedScopes.find(s => s.scope_id === f.scope_id);
              if (sc) setActiveScope(sc.scope_id);
            }}
            onMoveFlag={moveFlaggedLine}
            onResolveClarification={(c) =>
              setClarifications(prev => prev.map(x => x.id === c.id ? { ...x, resolved: !x.resolved } : x))}
            onRemoveClarification={(c) =>
              setClarifications(prev => prev.filter(x => x.id !== c.id))}
          />
        )}
        {aiMeta && (
          <AITakeoffNotes
            meta={aiMeta}
            onDismiss={() => setAiMeta(null)}
            onNavigateTarget={navigateToAINoteTarget}
            resolved={resolvedAINotes}
            onToggleResolved={toggleAINoteResolved}
          />
        )}
        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-3">
            <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-xs text-slate-500" data-testid="breadcrumbs">
              <button onClick={handleBack} className="hover:text-[#007BFF] hover:underline" data-testid="back-btn">Budgets</button>
              <ChevronRight className="h-3 w-3 opacity-60" />
            </nav>
            <h1 className="text-base font-semibold text-slate-800" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }} data-testid="budget-title">
              {projectInfo.project_name || "Untitled Budget"}
            </h1>
            {/* Budget-level actions: Summary / Export PDF / Email PDF / Delete (current scope) */}
            {!isNew && (
              <div className="flex items-center gap-2 ml-2 pl-3 border-l border-slate-200">
                <AITakeoffAddButton onMerge={mergeAdditionalTakeoff} disabled={isArchived} />
                <Button variant="outline" size="sm" onClick={() => setSummaryModalOpen(true)} className="rounded-sm border-slate-300 text-slate-800 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100 dark:hover:bg-slate-800 gap-1.5 text-xs" data-testid="view-summary-btn">
                  <FileText className="w-3.5 h-3.5" /> Summary
                </Button>
                <Button size="sm" onClick={handleExportPdf} className="rounded-sm bg-[#007BFF] hover:bg-[#0069d9] text-white gap-1.5 text-xs" data-testid="export-pdf-btn">
                  <Download className="w-3.5 h-3.5" /> Export PDF
                </Button>
                <Button variant="outline" size="sm" onClick={() => { if (user?.email && !emailTo) setEmailTo(user.email); setEmailModalOpen(true); }} className="rounded-sm border-0 bg-slate-200 text-slate-800 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600 gap-1.5 text-xs" data-testid="email-pdf-btn">
                  <Mail className="w-3.5 h-3.5" /> Email PDF
                </Button>
                {shareToken && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const url = `${window.location.origin}/share/b/${shareToken}`;
                        navigator.clipboard?.writeText(url);
                        toast.success("Share link copied to clipboard");
                      }}
                      className="rounded-sm border-0 bg-slate-200 text-slate-800 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600 gap-1.5 text-xs"
                      data-testid="copy-share-link-btn"
                      title="Public share link (works without login)"
                    >
                      <Link className="w-3.5 h-3.5" /> Copy Share Link
                    </Button>
                    {isAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/budgets/${id}/revoke-share`);
                            setShareToken("");
                            toast.success("Share link revoked");
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Revoke failed");
                          }
                        }}
                        className="rounded-sm border-red-200 text-red-600 hover:bg-red-50 gap-1.5 text-xs"
                        data-testid="revoke-share-link-btn"
                      >
                        Revoke
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && !isNew && (
              <Button
                size="sm"
                variant="outline"
                onClick={toggleShowPrices}
                className={`rounded-sm text-xs gap-1.5 ${showPrices ? "border-slate-300 text-slate-800 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100 dark:hover:bg-slate-800" : "border-slate-500 bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100"}`}
                data-testid="toggle-show-prices-btn"
                title={showPrices ? "Prices are visible — click to hide" : "Prices are hidden — click to reveal"}
              >
                {showPrices ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                {showPrices ? "Prices Shown" : "Prices Hidden"}
              </Button>
            )}
            {isAdmin && !isNew && (
              <Button size="sm" onClick={() => handleSave("submitted")} disabled={saving} className="rounded-sm text-xs" data-testid="submit-budget-btn">
                Save
              </Button>
            )}
          </div>
        </div>

        {/* Scope content */}
        <div className="flex-1 overflow-y-auto bg-[#F8FAFC]">
          {activeScopeData ? (
            <ScopePanel
              scope={activeScopeData}
              onUpdate={updateScope}
              onRemove={removeScope}
              isAdmin={isAdmin}
              showPrices={showPrices}
              onMoveFlag={moveFlaggedLine}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center py-20">
              <Layers className="w-12 h-12 text-slate-300 mb-4" />
              <h2 className="text-lg font-medium text-slate-600" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>No scope selected</h2>
              <p className="text-sm text-slate-400 mt-1 mb-4">Add a scope module from the left panel to begin configuring</p>
              <Button onClick={() => setSelectorOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm gap-2" data-testid="add-scope-main-btn">
                <Plus className="w-4 h-4" /> Add Scope
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Scope selector modal */}
      <ScopeSelector
        open={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        hierarchy={hierarchy}
        selectedIds={selectedScopes.map(s => s.scope_id)}
        onAdd={addScope}
      />

      {/* Popout Summary modal */}
      <Dialog open={summaryModalOpen} onOpenChange={setSummaryModalOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto p-0 print:max-w-none print:max-h-none print:overflow-visible [&>button]:hidden" data-testid="summary-modal">
          <DialogHeader className="no-print px-6 py-3 border-b border-slate-200 sticky top-0 bg-white z-20 flex flex-row items-center justify-between space-y-0">
            <div>
              <DialogTitle className="text-sm font-medium text-slate-700">Budget Summary</DialogTitle>
              <DialogDescription className="sr-only">A read-only summary of the current budget including project info, scope totals, and line-item details.</DialogDescription>
            </div>
            <DialogClose
              className="p-1.5 rounded-sm text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              data-testid="summary-modal-close-btn"
              aria-label="Close summary"
            >
              <X className="w-4 h-4" />
            </DialogClose>
          </DialogHeader>
          <div className="bg-[#F8FAFC]">
            <BudgetSummaryContent budget={liveBudget} defaultQualification={defaultQualification} showPrices={showPrices} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Email PDF modal */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent className="max-w-md" data-testid="email-pdf-modal">
          <DialogHeader>
            <DialogTitle className="text-base">Email Budget Summary</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">Send this budget as an HTML email. Recipients can save it as PDF from their email client.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">To</Label>
              <Input
                type="email"
                value={emailTo}
                onChange={e => setEmailTo(e.target.value)}
                placeholder="recipient@example.com"
                className="h-9 text-sm rounded-sm border-slate-200 focus:border-[#161616] focus:ring-1 focus:ring-[#161616]"
                data-testid="email-pdf-to-input"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Cc (comma-separated, optional)</Label>
              <Textarea
                value={emailCc}
                onChange={e => setEmailCc(e.target.value)}
                placeholder="recipient1@example.com, recipient2@example.com"
                className="text-sm rounded-sm border-slate-200 focus:border-[#161616] focus:ring-1 focus:ring-[#161616] min-h-[60px]"
                data-testid="email-pdf-cc-input"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Message (optional)</Label>
              <Textarea
                value={emailMessage}
                onChange={e => setEmailMessage(e.target.value)}
                placeholder="Add a short note for the recipient…"
                className="text-sm rounded-sm border-slate-200 focus:border-[#161616] focus:ring-1 focus:ring-[#161616] min-h-[60px]"
                data-testid="email-pdf-message-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setEmailModalOpen(false)} className="rounded-sm border-slate-300 text-slate-600 text-xs" data-testid="email-pdf-cancel-btn">
              Cancel
            </Button>
            <Button size="sm" disabled={emailSending} onClick={handleEmailSend} className="bg-[#007BFF] hover:bg-[#0069d9] text-white rounded-sm text-xs" data-testid="email-pdf-send-btn">
              {emailSending ? "Sending…" : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print-only container — portal-mounted on <body> so it renders outside
          the hidden #root during printing; 'Export PDF' produces an exact copy. */}
      {createPortal(
        <div className="print-only" aria-hidden="true">
          <BudgetSummaryContent budget={liveBudget} defaultQualification={defaultQualification} showPrices={showPrices} />
        </div>,
        document.body,
      )}
    </div>
  );
}
